import axios from 'axios';
import {
  AxiosInstanceWithVersioning,
  VersioningStrategy,
  withVersioning,
} from 'axios-api-versioning';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import authApi from '~/authApi';
import { useFlagStore } from '~/components/FeatureFlagControls/store';
import { FEATURE_FLAGS } from '~/constants';
import type { MMVUSearchResponse } from '~/screens/MMVUScreen/mmvu.types';
import {
  COMPARATOR_TYPES,
  SearchApiCreatorsRequestParams,
  SearchApiCreatorsRequestParamsMMVU,
  SearchApiCreatorsRequestParamsTTA,
  SearchCreatorsApiResponse,
  SearchCreatorsApiResponseTTA,
} from '~/searchApi';

class SearchCreatorApiClient {
  public _apiInstance: AxiosInstanceWithVersioning;

  private BASE_SEARCH_CREATOR_ENDPOINT_URL = '/creators';

  constructor() {
    const baseClient = axios.create({
      baseURL: `${process.env.NEXT_PUBLIC_SEARCH_RESOURCE}/v{apiVersion}`,
    });

    this._apiInstance = withVersioning(baseClient, {
      apiVersion: '1',
      versioningStrategy: VersioningStrategy.UrlPath,
    });

    this._apiInstance.interceptors.request.use((req) => {
      const { jwtAccessToken } = authApi;

      if (jwtAccessToken) {
        req.headers = {
          ...req.headers,
          'X-AUTH-TOKEN': jwtAccessToken.accessToken,
        };
      }

      return req;
    });

    createAuthRefreshInterceptor(baseClient, (failedRequest) =>
      authApi
        .refreshAccessToken()
        .then((newToken) => {
          failedRequest.response.config.headers['X-AUTH-TOKEN'] =
            newToken?.accessToken;
          if (newToken) {
            return Promise.resolve();
          } else {
            return Promise.reject();
          }
        })
        .catch(() => {
          return Promise.reject();
        }),
    );

    this._apiInstance.interceptors.request.use((req) => {
      const isTestDataEnabled = Boolean(
        useFlagStore.getState().flags[FEATURE_FLAGS.foamTestData]?.value,
      );

      if (isTestDataEnabled && !req.url?.includes('/creators/mmvu')) {
        req.params = { ...req.params, show_test: true };
      }

      return req;
    });
  }

  public async getCreators(
    params: SearchApiCreatorsRequestParams,
  ): Promise<SearchCreatorsApiResponse> {
    const { data } = await this._apiInstance.post(
      this.BASE_SEARCH_CREATOR_ENDPOINT_URL,
      params,
    );

    return data;
  }

  public async getCreatorById(id: string): Promise<SearchCreatorsApiResponse> {
    const { data } = await this._apiInstance.post(
      this.BASE_SEARCH_CREATOR_ENDPOINT_URL,
      {
        page: 1,
        q_fields: ['slug'],
        filters: [
          {
            comparator: COMPARATOR_TYPES.EQ,
            name: 'id',
            value: id,
          },
        ],
      },
    );

    return data;
  }

  public async getCreatorsTTA(
    params: SearchApiCreatorsRequestParamsTTA,
  ): Promise<SearchCreatorsApiResponseTTA> {
    const { data } = await this._apiInstance.post(
      `${this.BASE_SEARCH_CREATOR_ENDPOINT_URL}/tta`,
      params,
    );

    return data;
  }

  public async getCreatorsMMVU(
    params: SearchApiCreatorsRequestParamsMMVU,
  ): Promise<MMVUSearchResponse> {
    const { data } = await this._apiInstance.post(
      `${this.BASE_SEARCH_CREATOR_ENDPOINT_URL}/mmvu`,
      params,
    );

    return data;
  }

  public async getTTAContentDetails(
    contentId: string,
    docId: string,
  ): Promise<import('~/types/tta').TTAContentDetails> {
    const { data } = await this._apiInstance.get(
      `/contents/tta/${contentId}/${docId}`,
    );

    return data;
  }
}

export const searchCreatorApi = new SearchCreatorApiClient();
